import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, RefreshCcw } from 'lucide-react';
import { Button } from '../components/ui/button';
import { RendPlexLogo } from '../components/RendPlexLogo';
import LanguageSwitcher from '../components/LanguageSwitcher';
import DarkModeToggle from '../components/DarkModeToggle';
import { Footer } from '../components/LegalDisclaimers';

export default function RefundPolicy() {
  const { t } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#F8FAFC] dark:bg-[#0B0E14]">
      <nav className="border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0B0E14] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-4">
          <div className="flex justify-between items-center">
            <Link to="/" className="flex items-center gap-2">
              <RendPlexLogo size="md" />
            </Link>
            <div className="flex items-center gap-3">
              <DarkModeToggle />
              <LanguageSwitcher variant="minimal" />
              <Link to="/">
                <Button variant="ghost" className="text-slate-600 dark:text-slate-400">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  {t('refundPolicy.backToHome')}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-12 py-12">
        <div className="bg-white dark:bg-[#131824] border border-slate-200 dark:border-slate-800 rounded-2xl p-8 md:p-12">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-[#45C04A]/10 flex items-center justify-center">
              <RefreshCcw className="w-5 h-5 text-[#45C04A]" />
            </div>
            <h1
              data-testid="refund-policy-title"
              className="font-primary font-bold text-3xl md:text-4xl text-slate-900 dark:text-white"
            >
              {t('refundPolicy.title')}
            </h1>
          </div>
          <p className="text-slate-500 dark:text-slate-400 mb-8">{t('refundPolicy.lastUpdated')}</p>

          <div className="prose prose-slate dark:prose-invert max-w-none">
            <p className="text-slate-700 dark:text-slate-300 mb-6">{t('refundPolicy.intro')}</p>

            <h2 className="font-primary font-bold text-2xl text-slate-900 dark:text-white mt-8 mb-3">
              {t('refundPolicy.subscriptions.title')}
            </h2>
            <p className="text-slate-700 dark:text-slate-300 mb-4">{t('refundPolicy.subscriptions.body')}</p>

            <h2 className="font-primary font-bold text-2xl text-slate-900 dark:text-white mt-8 mb-3">
              {t('refundPolicy.cancellation.title')}
            </h2>
            <p className="text-slate-700 dark:text-slate-300 mb-4">{t('refundPolicy.cancellation.body')}</p>

            <h2 className="font-primary font-bold text-2xl text-slate-900 dark:text-white mt-8 mb-3">
              {t('refundPolicy.refunds.title')}
            </h2>
            <p className="text-slate-700 dark:text-slate-300 mb-3">{t('refundPolicy.refunds.body1')}</p>
            <p className="text-slate-700 dark:text-slate-300 mb-3">{t('refundPolicy.refunds.body2')}</p>
            <p className="text-slate-700 dark:text-slate-300 mb-4">{t('refundPolicy.refunds.body3')}</p>

            <h2 className="font-primary font-bold text-2xl text-slate-900 dark:text-white mt-8 mb-3">
              {t('refundPolicy.trials.title')}
            </h2>
            <p className="text-slate-700 dark:text-slate-300 mb-4">{t('refundPolicy.trials.body')}</p>

            <h2 className="font-primary font-bold text-2xl text-slate-900 dark:text-white mt-8 mb-3">
              {t('refundPolicy.howToCancel.title')}
            </h2>
            <p className="text-slate-700 dark:text-slate-300 mb-3">{t('refundPolicy.howToCancel.body')}</p>
            <p className="text-slate-700 dark:text-slate-300 mb-4">
              {t('refundPolicy.howToCancel.contact')}{' '}
              <a
                href="mailto:support@rendplex.com"
                data-testid="refund-policy-support-email"
                className="text-[#45C04A] underline"
              >
                support@rendplex.com
              </a>
            </p>

            <h2 className="font-primary font-bold text-2xl text-slate-900 dark:text-white mt-8 mb-3">
              {t('refundPolicy.consumerRights.title')}
            </h2>
            <p className="text-slate-700 dark:text-slate-300 mb-4">{t('refundPolicy.consumerRights.body')}</p>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
